import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

export interface VideoOptions {
    inline: boolean
    HTMLAttributes: Record<string, any>
    onVideoUpload?: (file: File) => void
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        video: {
            /**
             * Add a video
             */
            setVideo: (options: { src: string; controls?: boolean }) => ReturnType
        }
    }
}

export const VideoExtension = Node.create<VideoOptions>({
    name: 'video',

    addOptions() {
        return {
            inline: false,
            HTMLAttributes: {},
        }
    },

    inline() {
        return this.options.inline
    },

    group() {
        return this.options.inline ? 'inline' : 'block'
    },

    draggable: true,

    addAttributes() {
        return {
            src: {
                default: null,
            },
            controls: {
                default: true,
            },
            width: {
                default: null,
            },
            height: {
                default: null,
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'video',
            },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        return ['video', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
    },

    addCommands() {
        return {
            setVideo:
                options =>
                ({ commands }) => {
                    return commands.insertContent({
                        type: this.name,
                        attrs: options,
                    })
                },
        }
    },

    addProseMirrorPlugins() {
        const { onVideoUpload } = this.options

        return [
            new Plugin({
                key: new PluginKey('videoUpload'),
                props: {
                    handleDOMEvents: {
                        drop(view, event) {
                            const hasFiles =
                                event.dataTransfer &&
                                event.dataTransfer.files &&
                                event.dataTransfer.files.length > 0

                            if (!hasFiles) {
                                return false
                            }

                            const videos = Array.from(event.dataTransfer.files).filter(file =>
                                file.type.includes('video'),
                            )

                            if (videos.length === 0) {
                                return false
                            }

                            event.preventDefault()

                            const { schema } = view.state
                            const coordinates = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            })

                            videos.forEach(video => {
                                // Handle video upload through parent component
                                if (onVideoUpload) {
                                    onVideoUpload(video)
                                } else {
                                    // Fallback: create an object URL for immediate display
                                    const src = URL.createObjectURL(video)
                                    const node = schema.nodes.video.create({ src, controls: true })
                                    const transaction = view.state.tr.insert(
                                        coordinates?.pos || 0,
                                        node,
                                    )
                                    view.dispatch(transaction)
                                }
                            })

                            return true
                        },
                    },
                },
            }),
        ]
    },
})
